import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GALLERY_FOLDER_MAX_DEPTH,
  GALLERY_FOLDER_NAME_MAX_LENGTH,
  isSystemGalleryFolderKind,
  type GalleryFolder,
} from '@tailoring-crm/shared';
import { galleryFolderLabel } from '../../lib/gallery/gallery-folder-label.js';
import { useUpdateGalleryFolder } from '../../lib/gallery/use-gallery.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@tailoring-crm/ui';
import { buildFolderTree, flattenFolderTree } from './gallery-folder-tree.js';

/**
 * Renaming a folder — and, for a custom one only, moving it. Every folder, system or custom,
 * offers a rename; reparenting is a custom-folder affordance, because the four seeded folders
 * are the shape task 16 provisions and are not somewhere a shop moves around.
 */
export interface RenameFolderDialogProps {
  /** The folder being renamed, or null when the dialog is closed. */
  folder: GalleryFolder | null;
  folders: readonly GalleryFolder[];
  onOpenChange: (open: boolean) => void;
}

export function RenameFolderDialog({
  folder,
  folders,
  onOpenChange,
}: RenameFolderDialogProps): ReactElement | null {
  const { t } = useTranslation();
  const update = useUpdateGalleryFolder();
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (folder) {
      setName(folder.name);
      setParentId(folder.parentId ?? '');
      setError(null);
    }
  }, [folder]);

  const parentOptions = useMemo(
    () =>
      flattenFolderTree(buildFolderTree(folders)).filter(
        (node) => node.folder.id !== folder?.id && node.depth < GALLERY_FOLDER_MAX_DEPTH,
      ),
    [folders, folder?.id],
  );

  if (!folder) return null;
  const custom = !isSystemGalleryFolderKind(folder.kind);

  const submit = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setError(null);

    const patch: { name?: string; parentId?: string | null } = {};
    if (trimmed !== folder.name) patch.name = trimmed.slice(0, GALLERY_FOLDER_NAME_MAX_LENGTH);
    if (custom) {
      const nextParent = parentId || null;
      if (nextParent !== (folder.parentId ?? null)) patch.parentId = nextParent;
    }
    if (Object.keys(patch).length === 0) {
      onOpenChange(false);
      return;
    }

    try {
      await update.mutateAsync({ folderId: folder.id, patch });
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onOpenChange(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('gallery.rename.title', { name: galleryFolderLabel(t, folder) })}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {error ? (
            <p
              role="alert"
              className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
            >
              {error}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="gallery-rename-name">{t('gallery.addFolder.nameLabel')}</Label>
            <Input
              id="gallery-rename-name"
              value={name}
              maxLength={GALLERY_FOLDER_NAME_MAX_LENGTH}
              autoFocus
              onChange={(event) => setName(event.target.value)}
            />
          </div>

          {custom ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="gallery-rename-parent">{t('gallery.addFolder.parentLabel')}</Label>
              <select
                id="gallery-rename-parent"
                value={parentId}
                onChange={(event) => setParentId(event.target.value)}
                className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              >
                <option value="">{t('gallery.addFolder.noParent')}</option>
                {parentOptions.map(({ folder: candidate, depth }) => (
                  <option key={candidate.id} value={candidate.id}>
                    {'—'.repeat(depth - 1)} {galleryFolderLabel(t, candidate)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={!name.trim() || update.isPending}
            onClick={() => void submit()}
          >
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
